/**
 * Pipeline API - Daily Work Pipeline V1
 *
 * Endpoints for tenant settings, day sessions, work items, and reports.
 */

import { apiRequest } from './core.js';

/**
 * Pipeline API
 */
export const pipelineAPI = {
  // ============================================================
  // TENANT SETTINGS
  // ============================================================

  /**
   * Get tenant settings (including pipeline_enabled)
   * @returns {Promise<{tenant_id, pipeline_enabled, schedule}>}
   */
  getTenantSettings: () => apiRequest('/tenant/settings'),

  /**
   * Update tenant settings (admin only)
   * @param {Object} settings - { pipeline_enabled?: boolean, schedule?: {...} }
   * @returns {Promise<{tenant_id, pipeline_enabled, schedule}>}
   */
  updateTenantSettings: settings =>
    apiRequest('/tenant/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    }),

  // ============================================================
  // DAY SESSION
  // ============================================================

  /**
   * Get today's session status and counts
   * @returns {Promise<{date, current_time, session, counts, schedule}>}
   */
  getTodaySession: () => apiRequest('/pipeline/day/today'),

  /**
   * Open today's day session (starts the workday)
   * @returns {Promise<{message, day_session, rules_executed, counts}>}
   */
  openDay: () =>
    apiRequest('/pipeline/day/open', {
      method: 'POST',
    }),

  /**
   * Close today's day session (ends the workday, generates report)
   * @returns {Promise<{message, early_close, day_session, report_id, stats}>}
   */
  closeDay: () =>
    apiRequest('/pipeline/day/close', {
      method: 'POST',
    }),

  // ============================================================
  // WORK ITEMS
  // ============================================================

  /**
   * Get work items with optional filters
   * @param {Object} filters - { status?, priority?, type?, entity_kind?, include_snoozed? }
   * @returns {Promise<{data: WorkItem[], total: number}>}
   */
  getWorkItems: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.type) params.set('type', filters.type);
    if (filters.entity_kind) params.set('entity_kind', filters.entity_kind);
    if (filters.include_snoozed) params.set('include_snoozed', 'true');

    const query = params.toString();
    return apiRequest(`/pipeline/work-items${query ? '?' + query : ''}`);
  },

  /**
   * Create a manual work item
   * @param {Object} item - { title, description?, priority?, due_date?, entity_kind?, entity_id? }
   * @returns {Promise<WorkItem>}
   */
  createWorkItem: item =>
    apiRequest('/pipeline/work-items', {
      method: 'POST',
      body: JSON.stringify(item),
    }),

  /**
   * Perform action on a work item
   * @param {number} id - Work item ID
   * @param {string} action - DONE, BLOCK, SNOOZE, DISMISS, START, REOPEN
   * @param {Object} options - { note?, snoozed_until? }
   * @returns {Promise<{message, work_item}>}
   */
  workItemAction: (id, action, options = {}) =>
    apiRequest(`/pipeline/work-items/${id}/action`, {
      method: 'POST',
      body: JSON.stringify({ action, ...options }),
    }),

  /**
   * Log an action taken on a work item (with outcome tracking)
   * @param {number} id - Work item ID
   * @param {Object} data - { action_type, outcome, action_note?, contact_name?, contact_method?, followup_date? }
   * @returns {Promise<{ok: true, data: WorkItemActionLog, message}>}
   */
  logWorkItemAction: (id, data) =>
    apiRequest(`/pipeline/work-items/${id}/log-action`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Get action history for a work item
   * @param {number} id - Work item ID
   * @returns {Promise<{ok: true, data: WorkItemActionLog[]}>}
   */
  getWorkItemActions: id => apiRequest(`/pipeline/work-items/${id}/actions`),

  /**
   * Get action statistics for KPI dashboard
   * @param {Object} options - { days?: number }
   * @returns {Promise<{ok: true, data: ActionStats}>}
   */
  getActionStats: (options = {}) => {
    const params = new URLSearchParams();
    if (options.days) params.set('days', options.days);
    const query = params.toString();
    return apiRequest(`/pipeline/action-stats${query ? '?' + query : ''}`);
  },

  // ============================================================
  // REFRESH & REPORTS
  // ============================================================

  /**
   * Refresh: re-run rule engine to generate/update work items
   * @returns {Promise<{message, rules_executed, counts}>}
   */
  refresh: () =>
    apiRequest('/pipeline/refresh', {
      method: 'POST',
    }),

  /**
   * Get daily report by date
   * @param {string} date - YYYY-MM-DD format
   * @returns {Promise<{id, date, day_session_id, summary, created_at}>}
   */
  getDailyReport: date => apiRequest(`/pipeline/daily-report/${date}`),

  // ============================================================
  // RECOMMENDATION ACTIONS
  // ============================================================

  /**
   * Log an action taken on an entity to suppress recommendations
   * @param {Object} data - { entity_type, entity_id, recommendation_category?, action_type, action_note?, suppresses_for_hours? }
   * @returns {Promise<{ok: true, message, data: RecommendationAction}>}
   */
  logRecommendationAction: data =>
    apiRequest('/pipeline/recommendation-action', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Get recent recommendation actions
   * @param {Object} filters - { entity_type?, entity_id?, active_only? }
   * @returns {Promise<{ok: true, data: RecommendationAction[]}>}
   */
  getRecommendationActions: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.entity_type) params.set('entity_type', filters.entity_type);
    if (filters.entity_id) params.set('entity_id', filters.entity_id);
    if (filters.active_only !== undefined) params.set('active_only', filters.active_only);

    const query = params.toString();
    return apiRequest(`/pipeline/recommendation-actions${query ? '?' + query : ''}`);
  },

  /**
   * Remove/undo a recommendation action
   * @param {number} id - Action ID
   * @returns {Promise<{ok: true, message}>}
   */
  deleteRecommendationAction: id =>
    apiRequest(`/pipeline/recommendation-action/${id}`, {
      method: 'DELETE',
    }),

  // ============================================================
  // BRIEF FLOTTE
  // ============================================================

  /**
   * Get Brief Flotte - Daily fleet status report
   * @param {string} [date] - YYYY-MM-DD format (optional, defaults to today)
   * @returns {Promise<BriefFlotte>}
   */
  getBriefFlotte: date => apiRequest(`/pipeline/brief-flotte${date ? '/' + date : ''}`),
};

// Type definitions for IDE support
/**
 * @typedef {Object} WorkItem
 * @property {number} id
 * @property {number} tenant_id
 * @property {string} type - insurance_missing, doc_expiring, maintenance_due, claim_pending, license_expiring, vehicle_unassigned, custom_note
 * @property {string} title
 * @property {string} [description]
 * @property {string} priority - P0, P1, P2, P3
 * @property {string} status - OPEN, IN_PROGRESS, BLOCKED, DONE, SNOOZED, DISMISSED
 * @property {string} [due_date]
 * @property {string} [snoozed_until]
 * @property {number} [owner_user_id]
 * @property {string} [entity_kind] - vehicle, driver, claim, invoice, report, maintenance
 * @property {number} [entity_id]
 * @property {string} source - RULE_ENGINE, USER
 * @property {string} [rule_key]
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string} [resolved_at]
 */

/**
 * @typedef {Object} DaySession
 * @property {number} id
 * @property {string} status - OPEN, CLOSED
 * @property {string} opened_at
 * @property {string} [closed_at]
 */

/**
 * @typedef {Object} Schedule
 * @property {string} mode - flexible, fixed
 * @property {string} day_start_time - HH:MM
 * @property {string} day_end_time - HH:MM
 * @property {boolean} auto_close_enabled
 * @property {string} timezone
 */

/**
 * @typedef {Object} BriefFlotte
 * @property {string} title - Brief Flotte - formatted date
 * @property {string} date - YYYY-MM-DD
 * @property {string} generated_at - ISO timestamp
 * @property {string} session_status - NO_SESSION, OPEN, CLOSED
 * @property {Object} fleet_overview - Fleet statistics
 * @property {Object} claims_overview - Claims statistics
 * @property {Object} maintenance_overview - Maintenance statistics
 * @property {Object} work_items - { total, by_priority }
 * @property {Array} critical_alerts - P0 work items
 * @property {Array} recommendations - Smart recommendations
 */

/**
 * @typedef {Object} RecommendationAction
 * @property {number} id
 * @property {string} tenant_id
 * @property {string} entity_type - driver, vehicle, claim, maintenance_request
 * @property {number} entity_id
 * @property {string} [recommendation_category] - driver_availability, compliance, insurance, claims, maintenance, fleet
 * @property {string} action_type - contacted, email_sent, reviewed, acknowledged, deferred, resolved
 * @property {string} [action_note]
 * @property {string} [suppresses_until] - ISO datetime
 * @property {number} [created_by]
 * @property {string} created_at
 */

export default pipelineAPI;
